import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtInvited = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : null;

function MemberRow({ m, session, onSaved, setup }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(m.email || "");
  const [phone, setPhone] = useState(m.phone || "+91 ");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/[^\d+]/g, "");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Invalid email");
    if (clean && clean !== "+91" && !/^\+?\d{8,15}$/.test(clean)) return toast.error("Invalid WhatsApp number");
    setBusy(true);
    try {
      await api.updateContact(m.name, {
        editor: session.name,
        ...(email ? { email } : {}),
        ...(clean && clean !== "+91" ? { phone: clean } : {}),
      });
      toast.success(`${m.name}'s details saved`);
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.sendWhatsAppInvite(session.name, m.name);
      if (r.sent_to.length) toast.success(`Join link emailed to ${m.name}`);
      else toast.error(`${m.name} has no email on file yet`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally {
      setBusy(false);
    }
  };

  const invited = fmtInvited(m.invited_at);

  return (
    <li className="py-4" data-testid={`directory-row-${m.name.toLowerCase()}`}>
      <div className="flex items-start gap-4">
        <span className="mt-2 inline-block h-2 w-6 shrink-0" style={{ background: m.color }} />
        <div className="flex-1 min-w-0">
          <p className="font-display text-xl text-[#1A2F24] leading-tight flex items-center gap-2 flex-wrap">
            {m.name}
            {m.phone ? (
              <span className="tracker text-[9px] text-[#1A2F24] border border-[#DCE0D9] px-2 py-0.5" data-testid={`directory-wa-on-${m.name.toLowerCase()}`}>WhatsApp on</span>
            ) : (
              <span className="tracker text-[9px] text-[#9E473F] border border-[#9E473F]/50 px-2 py-0.5" data-testid={`directory-wa-off-${m.name.toLowerCase()}`}>No number</span>
            )}
          </p>
          {!editing && (
            <p className="font-body text-sm text-[#5C6B61] mt-1 break-words">
              {m.email || <span className="italic">no email yet</span>}
              {m.phone && <> · {m.phone}</>}
              {invited && <span className="text-[#5C6B61]/70"> · invited {invited}</span>}
            </p>
          )}
          {editing && (
            <form onSubmit={save} className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email"
                data-testid={`directory-email-${m.name.toLowerCase()}`}
                className="flex-1 bg-transparent border-b border-[#1A2F24] py-2 font-body text-base text-[#1A2F24] outline-none focus:border-[#9E473F]" />
              <input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxx xxxxx"
                data-testid={`directory-phone-${m.name.toLowerCase()}`}
                className="flex-1 bg-transparent border-b border-[#1A2F24] py-2 font-body text-base text-[#1A2F24] outline-none focus:border-[#9E473F]" />
              <div className="flex gap-2">
                <button type="submit" disabled={busy} data-testid={`directory-save-${m.name.toLowerCase()}`}
                  className="bg-[#1A2F24] text-[#FAF9F6] px-4 py-2.5 font-body text-[11px] tracker hover:bg-[#2C4A3A] transition-colors disabled:opacity-60">
                  Save
                </button>
                <button type="button" onClick={() => setEditing(false)}
                  className="border border-[#DCE0D9] px-4 py-2.5 font-body text-[11px] tracker text-[#5C6B61] hover:text-[#1A2F24] transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
        {!editing && (
          <div className="flex flex-col items-end gap-2 shrink-0">
            <button type="button" onClick={() => setEditing(true)} data-testid={`directory-edit-${m.name.toLowerCase()}`}
              className="font-body text-[10px] tracker text-[#1A2F24] hover:text-[#9E473F] transition-colors min-h-[32px]">
              Edit
            </button>
            {setup?.enabled && m.email && (
              <button type="button" onClick={invite} disabled={busy} data-testid={`directory-invite-${m.name.toLowerCase()}`}
                className="font-body text-[10px] tracker text-[#5C6B61] hover:text-[#9E473F] transition-colors min-h-[32px] disabled:opacity-60">
                {invited ? "Re-send join link" : "Email join link"}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export default function FamilyDirectory({ family, session, setup, onChanged }) {
  const [busy, setBusy] = useState(false);
  const withPhone = family.filter((m) => m.phone).length;

  const inviteAll = async () => {
    setBusy(true);
    try {
      const r = await api.sendWhatsAppInvite(session.name);
      toast.success(
        r.sent_to.length
          ? `Join link emailed to ${r.sent_to.length} ${r.sent_to.length === 1 ? "person" : "people"}`
          : "Nobody with an email left to invite"
      );
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invites");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-[#DCE0D9] p-5 sm:p-7 md:p-10" data-testid="family-directory">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="tracker text-[10px] text-[#5C6B61]">The Directory</p>
          <h2 className="font-display text-2xl sm:text-3xl text-[#1A2F24] mt-1">Who the house can reach</h2>
          <p className="font-body text-sm text-[#5C6B61] mt-2 max-w-lg">
            {withPhone} of {family.length} on WhatsApp. Anyone can fill in a number for anyone — then email them the
            one-tap join link.
          </p>
        </div>
        {setup?.enabled && (
          <button type="button" onClick={inviteAll} disabled={busy} data-testid="directory-invite-all"
            className="bg-[#1A2F24] text-[#FAF9F6] px-5 py-3 font-body text-[11px] tracker hover:bg-[#2C4A3A] transition-colors disabled:opacity-60">
            {busy ? "Sending…" : "Email join link to everyone"}
          </button>
        )}
      </div>

      {setup?.enabled && (
        <div className="mt-6 border border-[#DCE0D9] bg-[#FAF9F6] p-4 font-body text-sm text-[#1A2F24]" data-testid="directory-join-steps">
          <p className="tracker text-[10px] text-[#5C6B61] mb-2">How joining works</p>
          On WhatsApp, text <span className="font-medium">join {setup.join_code}</span> to{" "}
          <span className="font-medium whitespace-nowrap">{setup.sandbox_number}</span> — once, from the phone you want alerts on.
          The emailed link does this in one tap.
        </div>
      )}

      <ul className="mt-6 divide-y divide-[#DCE0D9] border-t border-[#DCE0D9]">
        {family.map((m) => (
          <MemberRow key={m.name} m={m} session={session} setup={setup} onSaved={onChanged} />
        ))}
      </ul>
    </section>
  );
}
